import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Clock,
  Users,
  Target,
  MoreVertical,
  Copy,
  Archive,
  Trash2,
  Edit,
  Calendar
} from 'lucide-react'
import { formatDate } from '@/lib/utils'

const PlanLibrary = ({ 
  plans, 
  selectedPlan, 
  onSelectPlan, 
  onDuplicatePlan, 
  onDeletePlan, 
  onArchivePlan 
}) => {
  const [activeDropdown, setActiveDropdown] = useState(null)

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-600 bg-green-100'
      case 'intermediate': return 'text-yellow-600 bg-yellow-100'
      case 'advanced': return 'text-red-600 bg-red-100'
      default: return 'text-slate-600 bg-slate-100'
    }
  }

  const getTotalExercises = (plan) => {
    return plan.sections.reduce((total, section) => total + section.exercises.length, 0)
  }

  const handleDropdownClick = (e, planId) => {
    e.stopPropagation()
    setActiveDropdown(activeDropdown === planId ? null : planId)
  }

  const handleActionClick = (e, action, planId) => {
    e.stopPropagation()
    setActiveDropdown(null)
    action(planId)
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {plans.length === 0 ? (
        <div className="p-6 text-center">
          <Target className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No Plans Found</h3>
          <p className="text-slate-600">
            Try adjusting your search or filters, or create a new plan to get started.
          </p>
        </div>
      ) : (
        <div className="p-4 space-y-3">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="relative"
            >
              <Card 
                className={`cursor-pointer transition-all duration-200 ${
                  selectedPlan?.id === plan.id 
                    ? 'ring-2 ring-blue-500 bg-blue-50' 
                    : 'hover:bg-slate-50'
                }`}
                onClick={() => onSelectPlan(plan)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">
                        {plan.name}
                      </h3>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                        {plan.description}
                      </p>
                    </div>
                    
                    <div className="relative ml-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleDropdownClick(e, plan.id)}
                        className="h-8 w-8 p-0"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                      
                      {activeDropdown === plan.id && (
                        <div className="absolute right-0 top-8 w-48 bg-white rounded-md shadow-sm border border-slate-200 py-1 z-10">
                          <button
                            onClick={(e) => handleActionClick(e, onDuplicatePlan, plan.id)}
                            className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center"
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate
                          </button>
                          <button
                            onClick={(e) => handleActionClick(e, onArchivePlan, plan.id)}
                            className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center"
                          >
                            <Archive className="h-4 w-4 mr-2" />
                            Archive
                          </button>
                          <button
                            onClick={(e) => handleActionClick(e, onDeletePlan, plan.id)}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                    <div className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      <span>{plan.duration} min</span>
                    </div>
                    <div className="flex items-center">
                      <Target className="h-3 w-3 mr-1" />
                      <span>{getTotalExercises(plan)} exercises</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(plan.difficulty)}`}>
                      {plan.difficulty}
                    </span>
                    
                    <div className="flex items-center text-xs text-slate-500">
                      <Users className="h-3 w-3 mr-1" />
                      <span>{plan.assignedClients.length} clients</span>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        <span>Created {formatDate(plan.createdAt)}</span>
                      </div>
                      {plan.updatedAt !== plan.createdAt && (
                        <span>Updated {formatDate(plan.updatedAt)}</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
      
      {/* Click outside to close dropdown */}
      {activeDropdown && (
        <div 
          className="fixed inset-0 z-5" 
          onClick={() => setActiveDropdown(null)}
        />
      )}
    </div>
  )
}

export default PlanLibrary